import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { NextSyncCountdown } from './NextSyncCountdown';
import { ThemeToggle } from './ThemeToggle';
import { API_URL } from '../config/apiConfig';
import { useAuth } from '../contexts/AuthContext';

// A URL da API é importada do arquivo de configuração para funcionar tanto em desenvolvimento quanto em produção

interface Store {
  id: string;
  name: string;
  status: string;
  lastSync: string | null;
  updateFlagValue: number | null;
  schedule: {
    isActive: boolean;
    interval: number | null;
  };
}

export const StoresList: React.FC = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [updatingApiKey, setUpdatingApiKey] = useState(false);
  const [apiKeyMessage, setApiKeyMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [storeOrder, setStoreOrder] = useState<string[]>([]);
  const [draggingStoreId, setDraggingStoreId] = useState<string | null>(null);
  const [dragOverStoreId, setDragOverStoreId] = useState<string | null>(null);

  const orderStorageKey = `storeCardOrder:${user?.id ?? 'anonymous'}`;

  const saveOrder = (nextOrder: string[]) => {
    try {
      localStorage.setItem(orderStorageKey, JSON.stringify(nextOrder));
    } catch (e) {
      // Ignore storage errors (private mode, quota, etc.)
      console.warn('Failed to persist store card order', e);
    }
  };

  const loadOrder = (): string[] => {
    try {
      const raw = localStorage.getItem(orderStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((v) => typeof v === 'string');
    } catch {
      return [];
    }
  };

  useEffect(() => {
    const fetchStores = async () => {
      try {
        // Tentar obter os dados da API
        const response = await axios.get(`${API_URL}/stores`, {
          // Adicionar timestamp para evitar cache
          params: { _t: new Date().getTime() }
        });
        
        // A API /stores retorna um array com objetos contendo id e name
        const storesData: Store[] = response.data;
        
        // Verificar se os dados obtidos têm o formato esperado
        if (Array.isArray(storesData)) {
          setStores(storesData);
          // Remover mensagem de erro caso existente
          if (error) setError(null);
        } else {
          throw new Error('Dados retornados pela API têm formato inválido');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stores:', error);
        
        // Se já temos dados, mantemos os dados existentes e apenas mostramos um aviso
        if (stores.length > 0) {
          setError('Aviso: Não foi possível atualizar os dados das lojas. Exibindo dados existentes.');
        } else {
          setError('Erro ao carregar lojas. Tente novamente mais tarde.');
          setLoading(false);
        }
      }
    };

    // Buscar dados imediatamente
    fetchStores();
    
    // Configurar polling para atualizar os dados a cada 60 segundos (aumentado de 30 para reduzir carga)
    const intervalId = setInterval(fetchStores, 60000);
    setPollingInterval(intervalId);
    
    // Limpar o intervalo quando o componente for desmontado
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dependência vazia intencional - queremos executar apenas na montagem

  useEffect(() => {
    // Load persisted order when the logged-in user changes.
    setStoreOrder(loadOrder());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // Keep persisted order in sync when the stores list changes (e.g., new store added).
    if (stores.length === 0) return;
    if (storeOrder.length === 0) return;

    const storesById = new Map(stores.map((s) => [s.id, s]));
    const defaultSortedIds = [...stores].sort((a, b) => a.name.localeCompare(b.name)).map((s) => s.id);
    const persistedOrder = storeOrder.filter((id) => storesById.has(id));
    const missingFromOrder = defaultSortedIds.filter((id) => !persistedOrder.includes(id));
    if (missingFromOrder.length === 0) return;

    const next = [...persistedOrder, ...missingFromOrder];
    if (next.length === storeOrder.length) return;
    setStoreOrder(next);
    saveOrder(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, storeOrder]);

  const handleNavigateToStore = (id: string) => {
    navigate(`/store/${id}`);
  };

  const defaultSortedStores = [...stores].sort((a, b) => a.name.localeCompare(b.name));

  const storesById = new Map(stores.map((s) => [s.id, s]));
  const persistedOrder = storeOrder.filter((id) => storesById.has(id));
  const missingFromOrder = defaultSortedStores
    .map((s) => s.id)
    .filter((id) => !persistedOrder.includes(id));
  const effectiveOrder = persistedOrder.length > 0 ? [...persistedOrder, ...missingFromOrder] : [];

  const displayStores = effectiveOrder.length > 0
    ? effectiveOrder.map((id) => storesById.get(id)!).filter(Boolean)
    : defaultSortedStores;

  const reorder = (sourceId: string, targetId: string) => {
    const base = (storeOrder.length > 0 ? storeOrder : defaultSortedStores.map((s) => s.id))
      .filter((id) => storesById.has(id));
    const fromIndex = base.indexOf(sourceId);
    const toIndex = base.indexOf(targetId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

    const next = [...base];
    next.splice(fromIndex, 1);
    next.splice(toIndex, 0, sourceId);
    setStoreOrder(next);
    saveOrder(next);
  };

  const handleResetOrder = () => {
    setStoreOrder([]);
    try {
      localStorage.removeItem(orderStorageKey);
    } catch (e) {
      console.warn('Failed to clear store card order', e);
    }
  };

  const handleDragStart = (e: React.DragEvent, storeId: string) => {
    setDraggingStoreId(storeId);
    setDragOverStoreId(null);
    try {
      // Some browsers (notably Safari) can be picky about the data type.
      e.dataTransfer.setData('text/plain', storeId);
      e.dataTransfer.setData('text', storeId);
      e.dataTransfer.effectAllowed = 'move';
    } catch {
      // no-op
    }
  };

  const handleDragEnd = () => {
    setDraggingStoreId(null);
    setDragOverStoreId(null);
  };

  const handleDragEnter = (e: React.DragEvent, storeId: string) => {
    e.preventDefault();
    if (dragOverStoreId !== storeId) setDragOverStoreId(storeId);
  };

  const handleDragOver = (e: React.DragEvent, storeId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStoreId !== storeId) setDragOverStoreId(storeId);
  };

  const handleDragLeave = (_e: React.DragEvent, storeId: string) => {
    if (dragOverStoreId === storeId) setDragOverStoreId(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text') || draggingStoreId;
    if (!sourceId || sourceId === targetId) return;
    reorder(sourceId, targetId);
    setDraggingStoreId(null);
    setDragOverStoreId(null);
  };

  const handleUpdateApiKey = async () => {
    setUpdatingApiKey(true);
    setApiKeyMessage({ type: 'info', text: 'Atualizando API Key...' });

    try {
      const response = await axios.post(`${API_URL}/api-key-updater/force-update`);
      
      if (response.data.success) {
        if (response.data.updated) {
          if (response.data.wasChanged) {
            setApiKeyMessage({ 
              type: 'success', 
              text: '✅ API Key atualizada com sucesso! (chave foi alterada)' 
            });
          } else {
            setApiKeyMessage({ 
              type: 'success', 
              text: '✅ API Key sincronizada com sucesso! (confirmada e atualizada)' 
            });
          }
        } else {
          setApiKeyMessage({ 
            type: 'error', 
            text: '❌ Falha ao atualizar API Key' 
          });
        }
      } else {
        setApiKeyMessage({ 
          type: 'error', 
          text: `❌ Erro: ${response.data.message || response.data.error || 'Erro desconhecido'}` 
        });
      }
    } catch (error: any) {
      console.error('Erro ao atualizar API Key:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Erro ao conectar com o servidor';
      setApiKeyMessage({ 
        type: 'error', 
        text: `❌ Erro: ${errorMessage}` 
      });
    } finally {
      setUpdatingApiKey(false);
      
      // Limpar mensagem após 5 segundos
      setTimeout(() => {
        setApiKeyMessage(null);
      }, 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Sync Dashboard</h1>
          {user?.email && (
            <p className="text-sm text-gray-600 mt-1">Logged in as: {user.email}</p>
          )}
          <p className="text-sm text-gray-500 mt-1">Dica: arraste o cabeçalho do card para reorganizar.</p>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button
            onClick={handleResetOrder}
            className="btn btn-secondary"
            title="Voltar para a ordem padrão (alfabética)"
          >
            Reset order
          </button>
          <button
            onClick={() => navigate('/search')}
            className="btn btn-secondary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Feeds
          </button>
          <button
            onClick={handleUpdateApiKey}
            disabled={updatingApiKey}
            className="btn bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Atualizar API Key manualmente"
          >
            {updatingApiKey ? (
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            )}
            {updatingApiKey ? 'Updating...' : 'Update API Key'}
          </button>
          <button
            onClick={() => signOut()}
            className="btn bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </div>
      
      {apiKeyMessage && (
        <div className={`px-4 py-3 rounded mb-6 border ${
          apiKeyMessage.type === 'success' ? 'bg-green-50 border-green-500 text-green-700' :
          apiKeyMessage.type === 'error' ? 'bg-red-50 border-red-500 text-red-700' :
          'bg-blue-50 border-blue-500 text-blue-700'
        }`}>
          <p className="font-medium">{apiKeyMessage.text}</p>
        </div>
      )}
      
      {error && (
        <div className="bg-error bg-opacity-10 border border-error text-error px-4 py-3 rounded mb-6">
          <p>{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
        {displayStores.map((store) => (
          <div
            key={store.id}
            className={[
              'card hover:shadow-lg transition-shadow duration-200',
              dragOverStoreId === store.id ? 'ring-2 ring-primary' : '',
              draggingStoreId === store.id ? 'opacity-60' : ''
            ].join(' ')}
            onDragEnter={(e) => handleDragEnter(e, store.id)}
            onDragOver={(e) => handleDragOver(e, store.id)}
            onDragLeave={(e) => handleDragLeave(e, store.id)}
            onDrop={(e) => handleDrop(e, store.id)}
          >
            <div className="flex justify-between items-start mb-4">
              <div
                className="flex items-start gap-3 cursor-grab"
                draggable={true}
                onDragStart={(e) => handleDragStart(e, store.id)}
                onDragEnd={handleDragEnd}
                title="Drag to reorder"
                aria-label={`Drag to reorder ${store.name}`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  draggable={false}
                  className="select-none text-gray-400 hover:text-gray-600 leading-none pt-1"
                >
                  ⋮⋮
                </div>
                <h2 className="text-xl font-semibold">{store.name}</h2>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  store.status === 'running' ? 'bg-success text-white' : 'bg-error text-white'
                }`}
              >
                {store.status === 'running' ? 'Running' : 'Stopped'}
              </span>
            </div>
            
            <div className="text-sm text-gray-600 mb-4">
              <p>Last synchronized: {store.lastSync ? new Date(store.lastSync).toLocaleString() : 'Never'}</p>
              {store.updateFlagValue !== null && store.updateFlagValue !== undefined && (
                <p className="font-semibold text-gray-800 mt-1">
                  Código de Atualização: <span className="text-red-600 font-bold">{store.updateFlagValue}</span>
                </p>
              )}
              <p>
                Agendamento: 
                {store.schedule && store.schedule.isActive 
                  ? (
                      <>
                        <span className="text-green-600 font-semibold"> Ativo ({store.schedule.interval}h)</span>
                        <NextSyncCountdown 
                          isActive={store.schedule.isActive} 
                          intervalHours={store.schedule.interval} 
                          lastSync={store.lastSync} 
                        />
                      </>
                    )
                  : <span className="text-red-600 font-semibold"> Inativo</span>
                }
              </p>
            </div>
            
            <div className="mt-auto pt-4 flex space-x-4">
              <button
                onClick={() => handleNavigateToStore(store.id)}
                className="btn btn-primary flex-1"
                aria-label={`Manage ${store.name}`}
              >
                Manage
              </button>
            </div>
          </div>
        ))}
        
        {stores.length === 0 && (
          <div className="col-span-full text-center p-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No stores found</p>
          </div>
        )}
      </div>
    </div>
  );
}; 
